import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  List,
  ListItem,
  ListItemText,
  IconButton,
} from '@mui/material';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import Tooltip from '@mui/material/Tooltip';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete'; // ✅ HERE
import DeleteIcon from '@mui/icons-material/Delete';

import { AgGridReact } from 'ag-grid-react';
import useFetchVendorsProcurement from '../../../hooks/useFetchVendorsProcurement';
import ConfirmDialog from '../../UserDialog/ConfirmDialog';
import axios from 'axios';
import AddNewContact from '../../VendorMgmt/AddNewContact';

const normalizedFilterOptions = createFilterOptions({
  matchFrom: 'any',
  stringify: (option) =>
    `${option.companyName || ''} ${option.vendorId || ''}`.replace(/[\s\u00A0]+/g, '').toLowerCase(),
});

const RfqVendorManagementModal = ({ open, onClose, rfqId }) => {
  const { vendors, loading: vendorsLoading } = useFetchVendorsProcurement();
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [assignedVendors, setAssignedVendors] = useState([]);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [vendorContacts, setVendorContacts] = useState([]);
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [unlockTarget, setUnlockTarget] = useState(null);
  const [unlockMessage, setUnlockMessage] = useState('');
  const [editContactsOpen, setEditContactsOpen] = useState(false);
  const [selectedContactVendor, setSelectedContactVendor] = useState(null);

  const gridRef = useRef();

  useEffect(() => {
    if (!rfqId || !open) return;

    const fetchAssigned = async () => {
      try {
        const res = await axios.get(`/api/VendorAssignedRfq/AssignedVendors/GetDataset/${rfqId}`);
        setAssignedVendors(res.data);
      } catch (err) {
        console.error('Failed to fetch assigned vendors:', err);
      }
    };

    fetchAssigned();
  }, [rfqId, open]);

  const handleAddVendor = async () => {
    if (!selectedVendor) return;

    try {
      const res = await axios.get(`/api/VendorContacts/get-contacts-by-vendorid/${selectedVendor.vendorId}`);
      setVendorContacts(res.data || []);
      setConfirmDialogOpen(true);
    } catch (err) {
      console.error('Failed to fetch vendor contacts:', err);
    }
  };

  const handleConfirmAddVendor = async () => {
    if (!selectedVendor || !rfqId) return;

    try {
      const payload = {
        nsVendorId: selectedVendor.nsVendorId,
        nsVendorName: selectedVendor.companyName,
        rfqId,
      };
      await axios.post(`/api/VendorAssignedRfq/PostRecord`, payload);
      const updated = await axios.get(`/api/VendorAssignedRfq/AssignedVendors/GetDataset/${rfqId}`);
      setAssignedVendors(updated.data || []);
      setSelectedVendor(null);
    } catch (err) {
      console.error(err);
      if (err.response?.status === 403) {
        toast.error('Elevated permissions required for this action.');
      } else {
        toast.error('Error assigning vendor');
      }
    } finally {
      setConfirmDialogOpen(false);
      setVendorContacts([]);
    }
  };

  const handleConfirmRevoke = async () => {
    if (!revokeTarget) return;
    try {
      await axios.delete(`/api/VendorAssignedRfq/Vendor/Delete/${revokeTarget}`);
      const updated = await axios.get(`/api/VendorAssignedRfq/AssignedVendors/GetDataset/${rfqId}`);
      setAssignedVendors(updated.data || []);
    } catch (err) {
      console.error(err);
      if (err.response?.status === 403) {
        toast.error('Elevated permissions required for this action.');
      } else {
        toast.error('Error revoking vendor');
      }
    } finally {
      setConfirmDialogOpen(false);
      setRevokeTarget(null);
    }
  };

  const handleCancelUnlock = () => {
    setUnlockTarget(null);
    setUnlockMessage('');
  };

  const handleSubmitUnlockBid = async () => {
    if (!unlockTarget || !unlockMessage.trim()) return;
    try {
      const payload = {
        bidId: unlockTarget,
        message: unlockMessage,
      };
      await axios.post('/api/VendorAssignedRfq/Vendor/UnlockBid', payload);
      const updated = await axios.get(`/api/VendorAssignedRfq/AssignedVendors/GetDataset/${rfqId}`);
      setAssignedVendors(updated.data || []);
    } catch (err) {
      console.error(err);
      if (err.response?.status === 403) {
        toast.error('Elevated permissions required for this action.');
      } else {
        toast.error('Failed to unlock bid');
      }
    } finally {
      setUnlockTarget(null);
      setUnlockMessage('');
    }
  };

  const columnDefs = [
    {
      headerName: 'Actions',
      pinned: 'left',
      cellRenderer: (params) => {
        const hasSubmitted = params.data.hasSubmittedBid === true;
        return (
          <Box display="flex" gap={1}>
            <Tooltip title="Edit Contacts">
              <IconButton
                onClick={() => {
                  const vendor = vendors.find((v) => v.vendorId === params.data.vendorId);
                  if (vendor) {
                    setSelectedContactVendor(vendor);
                    setEditContactsOpen(true);
                  }
                }}
                size="small"
              >
                <ContactMailIcon fontSize="small" color="primary" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Revoke Bid">
              <IconButton
                onClick={() => {
                  setRevokeTarget(params.data.id);
                  setConfirmDialogOpen(true);
                }}
                color="error"
                size="small"
              >
                <LocalFireDepartmentIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title={hasSubmitted ? 'Unlock Bid' : 'Pending Submission'}>
              <span>
                <Button
                  size="small"
                  color="success"
                  variant="contained"
                  style={{ fontSize: '10px' }}
                  disabled={!hasSubmitted}
                  onClick={() => {
                    setUnlockTarget(params.data.id);
                    setUnlockMessage('');
                  }}
                >
                  {hasSubmitted ? 'Unlock Bid' : 'Pending Submission'}
                </Button>
              </span>
            </Tooltip>
          </Box>
        );
      },

      width: 250,
    },
    { headerName: 'ID', field: 'id', hide: true },
    { headerName: 'NS Vendor ID', field: 'vendorId', hide: true },
    { headerName: 'Vendor Name', field: 'vendorName', width: 450 },
    {
      headerName: 'Veriforce Score',
      field: 'calculatedScore',
      width: 175,
      filter: false,
      cellRenderer: (params) => {
        const value = params.value || 'Unavailable';
        const color = params.data?.ssqColorCode || 'transparent';
        return (
          <span
            style={{
              backgroundColor: color,
              padding: '4px 8px',
              borderRadius: '4px',
              display: 'inline-block',
              width: '100%',
            }}
          >
            {value}
          </span>
        );
      },
    },
    {
      headerName: 'Veriforce Score Description',
      field: 'ssqScoreDescription',
      width: 275,
      cellRenderer: (params) => params.value || 'Unavailable',
    },
  ];

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl">
      <DialogContent sx={{ minHeight: '75vh', padding: '24px' }}>
        <Typography variant="h6">
          <strong>Assign Vendor(s) To RFQ</strong>
        </Typography>

        <Box display="flex" gap={2} mt={2} flexWrap="wrap" fontSize="0.875rem">
          {[
            { label: 'Green - Preferred', color: 'green' },
            { label: 'Orange - Caution', color: 'orange' },
            { label: 'Red - High Risk', color: 'red' },
            { label: 'White - Veriforce Data Unavailable', color: 'whitesmoke' },
          ].map(({ label, color }, i) => (
            <Box key={i} display="flex" alignItems="center" gap={1}>
              <Box
                width={12}
                height={12}
                borderRadius="50%"
                bgcolor={color}
                border={color === 'whitesmoke' ? '1px solid #ccc' : 'none'}
              />
              <span>{label}</span>
            </Box>
          ))}
        </Box>

        <Box mt={2} mb={2}>
          <Autocomplete
            filterOptions={normalizedFilterOptions}
            fullWidth
            size="small"
            disabled={vendorsLoading}
            options={vendors}
            value={selectedVendor}
            onChange={(_, newValue) => setSelectedVendor(newValue)}
            getOptionLabel={(option) =>
              typeof option === 'string' ? option : `${option.companyName} (${option.vendorId})`
            }
            isOptionEqualToValue={(option, value) => option.vendorId === value.vendorId}
            renderOption={(props, option) => (
              <li
                {...props}
                key={option.vendorId} // 🔐 unique key
                style={{ backgroundColor: option.ssqColorCode || 'transparent' }}
              >
                {option.companyName}
              </li>
            )}
            renderInput={(params) => <TextField {...params} label="Vendor" variant="outlined" />}
          />
        </Box>

        {selectedVendor && (
          <List dense>
            <ListItem
              sx={{ padding: '2px 4px' }}
              secondaryAction={
                <IconButton
                  edge="end"
                  size="small"
                  onClick={() => {
                    setSelectedVendor(null);
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              }
            >
              <ListItemText
                primary={selectedVendor.companyName}
                secondary={`Vendor ID: ${selectedVendor.vendorId}`}
                sx={{
                  backgroundColor: selectedVendor.ssqColorCode || 'transparent',
                  padding: '6px',
                  borderRadius: '4px',
                  fontSize: '0.875rem',
                }}
              />
            </ListItem>
          </List>
        )}

        <Box my={1}>
          <Button fullWidth variant="contained" color="success" onClick={handleAddVendor} disabled={vendorsLoading}>
            Add Vendor
          </Button>
        </Box>

        <Box height="300px">
          <AgGridReact
            ref={gridRef}
            rowData={assignedVendors}
            columnDefs={columnDefs}
            defaultColDef={{
              sortable: true,
              filter: true,
              resizable: true,
            }}
            domLayout="autoHeight"
          />
        </Box>

        {unlockTarget && (
          <Box mt={2}>
            <Typography variant="subtitle1" gutterBottom>
              Enter a message to send with the bid unlock:
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              value={unlockMessage}
              placeholder="Message to vendor"
              onChange={(e) => setUnlockMessage(e.target.value)}
            />
            <Box mt={2} display="flex" justifyContent="flex-end" gap={2}>
              <Button variant="contained" color="secondary" onClick={handleCancelUnlock}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmitUnlockBid}
                disabled={!unlockMessage.trim()}
              >
                Send Unlock
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained" color="secondary">
          Close
        </Button>
      </DialogActions>

      <ConfirmDialog
        open={confirmDialogOpen}
        message={
          revokeTarget ? (
            'Are you sure you want to revoke this vendor from the RFQ?'
          ) : (
            <Box>
              <Typography component="div" variant="body1" gutterBottom>
                The following contacts will be notified via email once the RFQ is enabled for bidding:
              </Typography>
              <Box component="ul" sx={{ mt: 1, pl: 2 }}>
                {vendorContacts.length ? (
                  vendorContacts.map((c, i) => (
                    <li key={i}>
                      {`${c.firstName ?? ''} ${c.lastName ?? ''}`.trim()} — {c.email ?? 'No email'}
                    </li>
                  ))
                ) : (
                  <li>No contacts found for this vendor.</li>
                )}
              </Box>
            </Box>
          )
        }
        onConfirm={revokeTarget ? handleConfirmRevoke : handleConfirmAddVendor}
        onCancel={() => {
          setConfirmDialogOpen(false);
          setVendorContacts([]);
          setRevokeTarget(null);
        }}
        onClose={() => {
          setConfirmDialogOpen(false);
          setVendorContacts([]);
          setRevokeTarget(null);
        }}
        showNo
        showCancel={false}
        confirmLabel={revokeTarget ? 'Yes, Revoke Vendor' : 'Assign Vendor'}
        noLabel="Back"
      />

      {editContactsOpen && selectedContactVendor && (
        <Dialog open={editContactsOpen} onClose={() => setEditContactsOpen(false)} fullWidth maxWidth="xl">
          <DialogContent>
            <AddNewContact vendor={selectedContactVendor} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditContactsOpen(false)} color="secondary" variant="contained">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Dialog>
  );
};

export default RfqVendorManagementModal;
